<template>
  <div class="text-body-2">
    <template v-if="items">
      <div
        v-for="item in itemsWithDividers"
        v-if="!item.key || (item.key != 'TAX') || parseInt(item.unit)"
        class="mb-1 relative"
      >
        <v-divider v-if="!item.key && !hideSeparators" class="my-3" />
        <template v-if="item.key">
          <div class="d-flex align-center relative">
            <div v-if="item.key == 'TAX'">{{item.meta.description}}</div>
            <div v-else-if="item.key == 'SUBSCRIPTION'">
              {{item.meta.code.charAt(0).toUpperCase() + item.meta.code.substr(1).toLowerCase()}} subscription
            </div>
            <div v-else>{{item.title || item.key}}</div>
            <v-btn
              v-if="item.disclaimer && item.disclaimer(items)"
              class="ml-1 my-n3"
              small icon color="primary"
              @click.stop.prevent="$confirm({
                msg: item.disclaimer(items),
                buttons: [ 'Ok' ],
                primary: 'Ok',
                persistent: false
              })"
            >
              <v-icon small v-html="mdiInformation" />
            </v-btn>
            <div v-if="item.qty != 1" class="ml-2 grey--text" style="white-space: nowrap;">× {{item.qty}}</div>
            <v-spacer />
            <price
              class="ml-2"
              :color="item.unit.startsWith('-') ? 'green' : 'black'"
              :value="(parseInt(item.unit.replace('.', '')) * item.qty / 100).toFixed(2)"
            />
          </div>
          <div
            class="ml-4 overline short grey--text pr-16 relative"
            style="font-size: .6rem !important; line-height: .8rem !important;"
          >
            <template v-if="item.key == 'GIFTCARD'">
              <currency :code="item.meta.cur" />{{item.meta.amt}} {{item.meta.name}}
            </template>
            <template v-else-if="item.key == 'CARD_CREDIT'">{{item.meta.code.split('@')[0]}}</template>
            <template v-else-if="item.key == 'BULK_PACK'">{{item.meta.code}}</template>
            <template v-else-if="item.key == 'CREDIT_PACK'">
              ${{item.meta.amt.split('.')[0]}} credit<template v-if="parseFloat(item.meta.amt) > 10"> at {{100 * (parseFloat(item.meta.amt) - parseFloat(item.unit)) / parseFloat(item.meta.amt)}}% off</template>
            </template>
            <template v-else-if="item.key == 'VIDEO_CREDIT'">{{item.meta.code}}</template>
            <template v-else-if="item.key == 'PCT_DISCOUNT'">
              {{(item.meta.pct * 100) + '% × ' + item.meta.subtotal}}
            </template>
            <template v-else-if="item.key == 'SUBSCRIPTION'">
              {{item.meta.cadence}}<span v-if="[ 'BUSINESS', 'MULTI-USER' ].includes(item.meta.code)"> • {{item.meta.max == 3 ? '1 – 3' : `up to ${item.meta.max}`}} users • {{getNSends(item.meta)}}</span>
            </template>
            <template v-else-if="item.key == 'SUBSCRIPTION_UPGRADE'">
              {{item.meta.cadence}}<span v-if="[ 'BUSINESS', 'MULTI-USER' ].includes(item.meta.code)"> • {{item.meta.max == 3 ? '1 – 3' : `up to ${item.meta.max}`}} users • {{getNSends(item.meta)}}</span>
            </template>
            <template v-else-if="item.key == 'SUBSCRIPTION_CREDIT'">
              {{item.meta.days_total - item.meta.days_elapsed}} / {{item.meta.days_total}} days unused
            </template>
          </div>
        </template>
      </div>
      <v-divider v-if="total" class="my-3" />
    </template>
    <div v-if="total" class="d-flex font-weight-bold">
      <div>Total</div>
      <v-spacer />
      <price :color="itemized.price.startsWith('-') ? 'red' : null" :value="itemized.price" />
    </div>
  </div>
</template>


<style>
.credit-packs {
  position: fixed;
  margin: 0 12px;
  transform: translate(0, -50%);
  background: #1E1E1E;
  color: #fff;
  border-radius: 4px;
}

.credit-packs::after {
  content: "";
  position: absolute;
  border-width: 5px;
  border-style: solid;
  top: 50%;
  right: 100%;
  margin-top: -5px;
  border-color: transparent #1E1E1E transparent transparent;
}
</style>


<script>
import CardPreview from '@/components/CardPreview.vue';
import Currency from '@/components/Currency.vue';
import ItemizedPricePromoCard from '@/components/ItemizedPricePromoCard.vue';
import Price from '@/components/Price.vue';
import { publicDesign } from '@/mixins/public-design.js';
import { publicGroupVideoTemplate } from '@/mixins/public-group-video-template.js';
import { mdiDelete, mdiInformation } from '@mdi/js';


// Note: if any of the types you want to display will display differently in its itemized row depending on its
// metadata, you need to implement the 'rowKey' function, which takes the item's metadata, and returns an identifier
// unique to its row. This allows us to combine rows that appear the same to the user, but might have different
// metadata.

const GIFTCARD_KEYS = [ 'GIFTCARD', 'GIFTCARD_FEE', 'CREDIT_GIFTCARD' ];
const DETAILS = {
  CARD_PERSONAL: {
    title: 'Personal card'
  },
  CARD_GROUP: {
    title: 'Group card'
  },
  CARD_INVITATION: {
    title: 'Event invitation'
  },
  GROUP_VIDEO: {
    title: 'Group video'
  },
  GIFTCARD: {
    title: 'Gift card',
    rowKey: meta => meta.amt + ' ' + meta.name
  },
  GIFTCARD_FEE: {
    title: 'Gift card service charge',
    // disclaimer: '$0.50 + 5% of gift card value'
  },
  RECIPIENT: {
    title: 'Recipient'
  },
  CONTRIBUTOR: {
    title: 'Contributor'
  },
  ADDTL_RECIPIENTS_BLOCK: {
    title: 'Block of 25 additional recipients'
  },
  ADDTL_CONTRIBUTORS_BLOCK: {
    title: 'Block of 25 additional contributors'
  },
  THEME_BG: {
    title: 'Custom confetti or background'
  },
  THEME_ENV: {
    title: 'Custom stamp, envelope color, or font'
  },
  THEME_LOGO: {
    title: 'Custom logo'
  },
  THEME_MUSIC: {
    title: 'Custom opening music'
  },
  PRIVATE_PAGES: {
    title: 'Private signing'
  },
  OPEN_MUSIC: {
    title: 'Opening music'
  },
  BULK_PACK: {
    title: 'Bulk pack',
    rowKey: meta => meta.code
  },
  CARD_CREDIT: {
    title: 'Card credit',
    subtitle: 'CARD_CREDIT',
    rowKey: meta => meta.code
  },
  VIDEO_CREDIT: {
    title: 'Video credit',
    rowKey: meta => meta.code
  },
  CREDIT: {
    title: 'Credit',
    disclaimer: items => items.some(x => GIFTCARD_KEYS.includes(x.key)) ? 'Credit does not apply to gift cards' : null
  },
  CREDIT_GIFTCARD: {
    title: 'Credit (gift cards)'
  },
  CREDIT_PACK: {
    title: 'Pre-paid Ellacard credit'
  },
  CREDIT_SEND: {
    title: 'Credited contributors / recipients'
  },
  CREDIT_API: {
    title: 'API credit'
  },
  PCT_DISCOUNT: {
    title: 'Percent discount',
    disclaimer: items => items.some(x => GIFTCARD_KEYS.includes(x.key)) ? 'Percent discounts do not apply to gift cards' : null,
    rowKey: meta => meta.pct + ' ' + meta.subtotal
  },
  SUBSCRIPTION: {
    disclaimer: items => 'This is a recurring payment – you will be billed automatically until you change / cancel your subscription'
  },
  SUBSCRIPTION_CREDIT: {
    title: 'Existing subscription credit'
  },
  SUBSCRIPTION_DISCOUNT: {
    title: 'Subscription discount'
  },
  SUBSCRIPTION_UPGRADE: {
    title: 'Subscription upgrade'
  }
};


export default {
  title: 'itemized-price',

  mixins: [ publicDesign, publicGroupVideoTemplate ],

  components: { ItemizedPricePromoCard, CardPreview, Currency, Price },

  props: {
    itemized: Object,
    finalized: Boolean,
    hideSeparators: Boolean,
    total: { type: Boolean, default: true }
  },

  data() {
    return {
      designs: {},
      videos: {}
    };
  },

  computed: {
    items() {
      if (!this.itemized.items)
        return null;
      const items = this.itemized.items.map(x => ({ ...x, ...DETAILS[x.key] }));
      const itemsByRowKey = {};
      for (const item of items) {
        if (item.key == 'CUSTOM')
          item.title = item.meta.desc;
        const rowKey = item.key + ' ' + item.unit + (item.rowKey ? ' ' + item.rowKey(item.meta) : '');
        if (itemsByRowKey[rowKey])
          itemsByRowKey[rowKey].qty += item.qty;
        else
          itemsByRowKey[rowKey] = item;
      }
      return Object.values(itemsByRowKey);
    },

    selectedPack() {
      for (const item of this.items)
        if (item.key == 'CREDIT_PACK')
          return item.meta.amt;
      return null;
    },

    subtotal() {
      let subtotal = 0;
      for (const item of this.items)
        if (![ 'CREDIT_PACK', 'CREDIT_MIN_TRANSACTION' ].includes(item.key))
          subtotal += parseInt(item.unit.replace('.', '')) * item.qty;
      return (subtotal / 100).toFixed(2);
    },

    isCreditPack() {
      return this.items.length == 1 && this.items[0].key == 'CREDIT_PACK';
    },

    itemsWithDividers() {
      const items = [];
      let foundNonGiftcardItem = false;
      let didAddDivider = false;
      for (const item of this.items) {
        if (foundNonGiftcardItem && !didAddDivider && GIFTCARD_KEYS.includes(item.key)) {
          items.push({});
          didAddDivider = true;
        }
        items.push(item);
        if (!foundNonGiftcardItem && !GIFTCARD_KEYS.includes(item.key))
          foundNonGiftcardItem = true;
      }
      return items;
    }
  },

  created() {
    Object.assign(this, { mdiDelete, mdiInformation });
  },

  methods: {
    getNSends(meta) {
      if (meta.n_sends)
        return meta.n_sends;
      if ([ 'SINGLE-USER', 'MULTI-USER' ].includes(meta.code))
        return 'unlimited';
      let n = 0;
      if (meta.code == 'INDIVIDUAL')
        n = 500;
      else if (meta.code == 'BUSINESS')
        n = (meta.max < 25) ? 750 : (1000 + Math.floor(meta.max / 25) * 1000);
      return (meta.cadence == 'MONTHLY') ? Math.floor(n / 12) : n;
    }
  },

  watch: {
    items: {
      immediate: true,
      async handler(v) {
        for (const item of v || []) {
          if (item.subtitle == 'DESIGN')
            this.designs = { ...this.designs, [item.meta.design_id]: await this.getPublicDesign(item.meta.design_id) };
          else if (item.subtitle == 'VIDEO')
            this.videos = { ...this.videos, [item.meta.template_id]: await this.getPublicGroupVideoTemplate(item.meta.template_id) };
        }
      }
    }
  }
}
</script>
